import type { UtteranceSource } from '../types';

/**
 * `TtsProvider` per `specs/10-contracts.md`, with one addition flagged in the plan (C20): `SpeakOpts`
 * carries `source`. `UtteranceSpoken` requires it and only the caller knows whether a sentence
 * came from the keyboard, a phrase tile, a prediction or a quick fire.
 */

export type ProviderId = 'cachedAudio' | 'azureTts' | 'piperLocal' | 'webSpeech' | 'visualOnly';

export interface SpeakOpts {
  /** Global STOP. Must abort within ~100 ms (non-negotiable §3.3). */
  signal: AbortSignal;
  /** Eligible for caching and pre-render. */
  isPhrase: boolean;
  source: UtteranceSource;
  voiceId?: string;
}

export interface SpeakResult {
  providerId: ProviderId;
  /** For latency diagnostics — metadata only, never the text (NFR7). */
  startedAtMs: number;
  cached: boolean;
  /** Playback reported no progress. Suspect silent mode; warn the person. */
  silent?: boolean;
  /**
   * `performance.now()` at the moment sound actually began, if this provider can know.
   *
   * Absent, never zero, for a provider that cannot say — `visualOnly` makes no sound and must not
   * report having made it instantly. `speak()` resolves at the *end* of an utterance, so this is the
   * only honest source for `DIA-01`'s `msToAudio` (C80).
   *
   * Note it is a `performance.now()` reading rather than a duration, and so is comparable only with
   * other readings from the same clock. `startedAtMs` above is already inconsistent about that —
   * `PiperLocal` sets it from `Date.now()` — which is exactly why this is a separate field rather
   * than a second meaning for that one.
   */
  audioAtMs?: number;
}

export interface TtsContext {
  online: boolean;
  /**
   * False for dwell and scan activations. Phase 0 validated that Web Audio survives a timed
   * activation, but `speechSynthesis.speak()` does not — so this flag is what stops `WebSpeech`
   * being chosen for an activation it would silently drop.
   */
  inGesture: boolean;
  audioUnlocked: boolean;
}

export interface TtsProvider {
  readonly id: ProviderId;
  isAvailable(ctx: TtsContext): boolean;
  speak(text: string, opts: SpeakOpts): Promise<SpeakResult>;
  /** Optional: render without playing, for the pre-render pass. */
  prerender?(text: string, voiceId: string): Promise<Blob>;
}

/** Thrown by a provider that cannot serve this utterance, so the chain moves on. */
export class ProviderUnavailable extends Error {
  constructor(
    readonly providerId: ProviderId,
    reason: string,
  ) {
    super(`${providerId}: ${reason}`);
    this.name = 'ProviderUnavailable';
  }
}
